import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { dfpHistoricalHighlightsResponse } from '@interfaces/dfp/response/dfpHistoricalHighlightsResponse';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import { TableAbstractComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-abstract/table-abstract.component';
import { TableExportService } from '@src/app/shared/components/table/services/table-export.service';
import { DfpColumn, dfpColumnKey } from '@models/dfp/dfp-column.model';
import { DFP_SCALE_TITLE_NOTE_MIXED_UNITS } from '@app/features/dashboard/performance/pages/dfp/models/dfp-value-scale.model';
import { SharedModule } from '@src/app/shared/shared.module';
import { DfpPanelComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/dfp-panel/dfp-panel.component';
import { PeriodColumnAddComponent } from '@components/period-column-add/period-column-add.component';
import { YearColumnMenuComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/year-column-menu/year-column-menu.component';
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { DfpColumnWidthDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-column-width.directive';
import { DfpClipGuardDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-clip-guard.directive';
import { TopHorizontalScrollbarDirective } from '@shared/directives/top-horizontal-scrollbar.directive';
import {
  DfpCalculationColumn,
  DfpCalculationInfoComponent,
} from '@src/app/features/dashboard/performance/pages/dfp/components/calculation-info/calculation-info.component';
import {
  DfpNetDebtCompositionRow,
  buildNetDebtCompositionRows,
} from '@app/features/dashboard/performance/pages/dfp/utils/dfp-net-debt-composition.util';
import { DFP_EXPORT_LEVEL } from '@app/features/dashboard/performance/pages/dfp/utils/dfp-sub-account-rows.util';
import { NotApplicableValueComponent } from '@src/app/features/dashboard/performance/shared/not-applicable/not-applicable-value.component';
import { notApplicableReasonOf } from '@src/app/features/dashboard/performance/shared/not-applicable/not-applicable.util';

interface HighlightsHistorical {
  name?: string;
  years: dfpHistoricalHighlightsResponse[];
}

interface GroupByHistoricalHighlights {
  name?: string;
  hasSubItems?: boolean;
  groupPrefix?: string;
  historic: HighlightsHistorical[];
}

@Component({
  selector: 'app-table-historical-highlights',
  templateUrl: './table-historical-highlights.component.html',
  styleUrls: ['../table-abstract/table-abstract.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TableModule,
    PanelModule,
    MenuModule,
    PeriodColumnAddComponent,
    YearColumnMenuComponent,
    DfpPanelComponent,
    DfpCalculationInfoComponent,
    NotApplicableValueComponent,
    DfpColumnWidthDirective,
    DfpClipGuardDirective,
    TopHorizontalScrollbarDirective,
  ],
})
export class TableHistoricalHighlightsComponent extends TableAbstractComponent {
  /**
   * Colunas do balão de memória de cálculo de um destaque (card 3593), com o resultado formatado como a célula
   * (dinheiro na escala; dias e múltiplos no valor cheio).
   */
  calculationColumnsForHighlight(name: string): DfpCalculationColumn[] {
    return this.calculationColumnsFrom(
      this.tableData.filter(row => row.name === name),
      row => this.formatHighlightValue(name, row.value)
    );
  }

  /**
   * Formata a parcela do balão de um destaque. Nos ciclos as parcelas são prazos em dias (valor cheio); no resto são
   * dinheiro (Dívida Líquida, EBITDA, Ativo Circulante...), na escala da tabela, inclusive as parcelas da
   * Dívida Líquida / EBITDA.
   */
  formatHighlightPartFor(name: string): (label: string, value: number) => string {
    const isCycle = /ciclo/i.test(name ?? '');
    return (_label, value) => (isCycle ? this.formatOriginalValue(value) : this.formatValue(value));
  }
  @Input() companyName: string = '';
  override tableData: dfpHistoricalHighlightsResponse[] = [];
  groupedHistoricalHighlights: GroupByHistoricalHighlights[] = [];

  /**
   * Indicadores desta tabela que **não são dinheiro** e por isso ficam fora da escala de milhares: Ciclo
   * Operacional e Ciclo Financeiro são **dias**, e Dívida Líquida/EBITDA é **múltiplo**. Dividi-los por mil
   * exibiria `0` no lugar de `324` dias e de `1,45x` — número errado, não número resumido.
   *
   * ⚠️ **A distinção vai por NOME porque o contrato não traz unidade.** `dfpHistoricalHighlightsResponse` tem
   * só `name`, `value` e `groupPrefix`; não há campo de unidade, e `isAnnualized` não serve (é verdadeiro
   * apenas em coluna de período, justamente para estes três — mas nunca em coluna anual). Consequência
   * assumida: se o backend renomear um destes indicadores, ele passa a ser escalado, e **silenciosamente** —
   * a tela não tem como perceber. O conserto definitivo é o contrato declarar a unidade de cada indicador.
   *
   * As chaves são normalizadas (sem acento, sem espaço, minúsculas) porque a grafia varia entre o backend e a
   * tela — "Dívida Líquida / EBITDA" e "Dívida Líquida/EBITDA" são o mesmo indicador.
   */
  private static readonly NON_MONETARY_KEYS: ReadonlySet<string> = new Set([
    'ciclooperacional',
    'ciclofinanceiro',
    'dividaliquida/ebitda',
  ]);

  constructor(pdfUtilsService: PdfUtilsServiceV2, tableExportService: TableExportService) {
    super(pdfUtilsService, tableExportService);
  }

  /**
   * Formata um valor desta tabela respeitando a unidade do indicador: dinheiro entra na escala escolhida,
   * dias e múltiplos ficam sempre no valor original (ver {@link NON_MONETARY_KEYS}).
   *
   * Existe porque "Pontos de destaque" é a única tabela da tela que mistura unidades na mesma coluna — nas
   * outras três, toda célula é moeda e o `formatValue` da base basta.
   */
  formatHighlightValue(name: string, value: number | string | null): string {
    return TableHistoricalHighlightsComponent.isMonetary(name)
      ? this.formatValue(value)
      : this.formatOriginalValue(value);
  }

  /**
   * Nota de unidade deste painel. Não usa o padrão da base porque aqui a escala **não** vale para tudo: o
   * "(em milhares)" genérico afirmaria que Ciclo Operacional passou a ser medido em milhares de dias.
   */
  override get scaleTitleNote(): string {
    return DFP_SCALE_TITLE_NOTE_MIXED_UNITS[this.valueScale];
  }

  /** Normaliza o nome do indicador para comparação: sem acento, sem espaço, minúsculo. */
  private static isMonetary(name: string): boolean {
    const key = (name ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();

    return !TableHistoricalHighlightsComponent.NON_MONETARY_KEYS.has(key);
  }

  override groupTableData(): void {
    const groupByIncomeStatement = this.tableData.reduce((acc, sales) => {
      const group = acc.find(
        (group: GroupByHistoricalHighlights) => group.groupPrefix === sales.groupPrefix
      );
      if (group) {
        const sale = group.historic.find(s => s.name === sales.name);
        if (sale) {
          // Chave é (ano, período), não só o ano: com coluna de período o mesmo ano chega mais de uma vez
          // (consolidado + cada recorte). Comparar só por ano faria a linha de período SOBRESCREVER o valor
          // consolidado e deixar a coluna do período vazia.
          const yearSale = sale.years.find(y => dfpColumnKey(y) === dfpColumnKey(sales));
          if (yearSale) {
            yearSale.value = sales.value;
          } else {
            sale.years.push({
              ...sales,
              year: sales.year,
              value: sales.value,
            });
          }
        } else {
          group.historic.push({
            name: sales.name,
            years: [
              {
                ...sales,
                year: sales.year,
                value: sales.value,
              },
            ],
          });
        }
        if (sales.name !== group.name) {
          group.hasSubItems = true;
        }
      } else {
        acc.push({
          name: sales.name,
          groupPrefix: sales.groupPrefix,
          historic: [
            {
              name: sales.name,
              years: [
                {
                  ...sales,
                  year: sales.year,
                  value: sales.value,
                },
              ],
            },
          ],
        });
      }
      return acc;
    }, [] as GroupByHistoricalHighlights[]);

    this.groupedHistoricalHighlights = groupByIncomeStatement;
    this.compositionByGroup = new Map(
      this.groupedHistoricalHighlights.map(group => [
        group,
        buildNetDebtCompositionRows(group.historic.flatMap(sale => sale.years)),
      ])
    );
  }

  /** Composição da Dívida Líquida por grupo (card 3669), montada no agrupamento. Vazia nas outras linhas. */
  private compositionByGroup = new Map<GroupByHistoricalHighlights, DfpNetDebtCompositionRow[]>();

  /** Grupos com a composição aberta pelo "+" (começam fechados). */
  private readonly openCompositions = new Set<GroupByHistoricalHighlights>();

  /** Parcelas da Dívida Líquida da linha; vazio nas linhas que não são de dívida líquida (sem "+"). */
  compositionFor(group: GroupByHistoricalHighlights): DfpNetDebtCompositionRow[] {
    return this.compositionByGroup.get(group) ?? [];
  }

  isCompositionOpen(group: GroupByHistoricalHighlights): boolean {
    return this.openCompositions.has(group);
  }

  /** "+" da Dívida Líquida: abre ou fecha as parcelas. */
  toggleComposition(group: GroupByHistoricalHighlights): void {
    if (!this.compositionFor(group).length) return;
    if (this.openCompositions.has(group)) {
      this.openCompositions.delete(group);
    } else {
      this.openCompositions.add(group);
    }
  }

  /** Parcela na coluna, em dinheiro na escala da tabela; vazio sem valor naquela coluna. */
  compositionValue(row: DfpNetDebtCompositionRow, column: DfpColumn): string {
    const value = row.signedValuesByColumn[column.key];
    return value === undefined ? '' : this.formatValue(value);
  }

  readonly exportLevel = DFP_EXPORT_LEVEL;

  /** Motivo do "Não se aplica" da célula (holding pura: EBITDA e Dívida Líquida / EBITDA); nulo sem a marca. */
  notApplicableReasonIn(
    sales: dfpHistoricalHighlightsResponse[],
    column: DfpColumn
  ): string | null {
    return notApplicableReasonOf(this.findRowInColumn(sales, column));
  }

  /**
   * Valor da linha na coluna informada. A coluna consolidada casa com as linhas sem período; uma coluna de
   * período casa apenas com as linhas daquele par de meses, para nunca misturar recorte.
   */
  getValueByColumn(sales: dfpHistoricalHighlightsResponse[], column: DfpColumn): number | string {
    return this.findRowInColumn(sales, column)?.value ?? 0;
  }

  /** Indica se o valor daquela célula foi anualizado, para o template exibir o selo. */
  isAnnualizedInColumn(sales: dfpHistoricalHighlightsResponse[], column: DfpColumn): boolean {
    return this.findRowInColumn(sales, column)?.isAnnualized ?? false;
  }

  /**
   * Localiza a linha que pertence à coluna. Aceita tanto a lista já achatada quanto a estrutura agrupada
   * (`{ years: [...] }`) que `groupTableData` monta, porque o template usa as duas formas.
   */
  private findRowInColumn(
    sales: dfpHistoricalHighlightsResponse[],
    column: DfpColumn
  ): dfpHistoricalHighlightsResponse | undefined {
    if (!sales || sales.length === 0) return undefined;

    const flat: dfpHistoricalHighlightsResponse[] =
      'years' in sales[0]
        ? (sales as unknown as { years?: dfpHistoricalHighlightsResponse[] }[]).flatMap(
            sale => sale.years ?? []
          )
        : sales;

    return flat.find(row => dfpColumnKey(row) === column.key);
  }

  override exportToPdf(): void {
    this.exportPerformanceTableToPdf({
      filename: 'pontos-destaque.pdf',
      title: 'Pontos de destaque',
      subtitle: this.companyName,
    });
  }

  override exportToExcel(): void {
    this.exportPerformanceTableToExcel({
      selector: '.performance-table--highlights',
      sheetName: 'Pontos de destaque',
      filenamePrefix: 'pontos-destaque',
      levelColumn: true,
    });
  }
}
